import { useEffect, useRef, useState, type FormEvent, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { ChevronDown } from "lucide-react";
import { FaFacebook, FaInstagram, FaLinkedin, FaXTwitter, FaYoutube } from "react-icons/fa6";
import { useAppSettings } from "../hooks/useContactInfo";
import usePrefersReducedMotion from "../hooks/usePrefersReducedMotion";
import useCookieConsent from "../hooks/useCookieConsent";
import SuccessCheckmark from "./SuccessCheckmark";
import { subscribeToNewsletter } from "../utils/newsletter";

const EXPLORE = [
  { to: "/menu", key: "nav.menu" },
  { to: "/about", key: "footer.ourStory" },
  { to: "/products", key: "footer.productsServices" },
  { to: "/locations", key: "nav.locations" },
  { to: "/success-stories", key: "footer.successStories" },
  { to: "/contact", key: "nav.contact" },
] as const;

const COMPANY = [
  { to: "/compliance", key: "nav.compliance" },
  { to: "/sustainability", key: "footer.sustainability" },
  { to: "/legal", key: "footer.legalRegistrations" },
  { to: "/accessibility", key: "footer.accessibility" },
  { to: "/press-investors", key: "footer.pressInvestors" },
  { to: "/partnerships", key: "footer.partnerships" },
  { to: "/security", key: "footer.security" },
  { to: "/privacy", key: "footer.privacyPolicy" },
  { to: "/terms", key: "footer.termsConditions" },
] as const;

// TODO(backend): swap in ZenFresh's real, verified profile URLs once they
// exist — these are honest placeholders, not fabricated account links.
// Only links staff have published (Admin → App settings) are shown: an unset one used to be a dead "#" icon.
const SOCIAL_ICONS = [
  { Icon: FaInstagram, label: "Instagram", key: "instagram" },
  { Icon: FaLinkedin, label: "LinkedIn", key: "linkedin" },
  { Icon: FaXTwitter, label: "X (Twitter)", key: "x" },
  { Icon: FaYoutube, label: "YouTube", key: "youtube" },
  { Icon: FaFacebook, label: "Facebook", key: "facebook" },
] as const;

interface FooterSectionProps {
  title: string;
  children: ReactNode;
}

/**
 * Full column at md+; collapses into a tap-to-expand accordion row below md.
 * The toggle button itself is md:hidden (not just visually inert) so it
 * can't be tabbed to or misreport aria-expanded once the section is always
 * open at md+.
 */
function FooterSection({ title, children }: FooterSectionProps) {
  const [open, setOpen] = useState(false);
  const reducedMotion = usePrefersReducedMotion();

  return (
    <div className="border-b border-white/10 py-4 md:border-0 md:py-0">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="flex w-full items-center justify-between text-left md:hidden"
      >
        <h4 className="font-display text-sm font-semibold uppercase tracking-wider text-foam-50">{title}</h4>
        <ChevronDown className={`h-4 w-4 text-foam-100/60 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      <h4 className="hidden font-display text-sm font-semibold uppercase tracking-wider text-foam-50 md:block">
        {title}
      </h4>

      <div className="hidden md:mt-4 md:block">{children}</div>

      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: reducedMotion ? 0 : 0.25, ease: "easeInOut" }}
            className="overflow-hidden md:hidden"
          >
            <div className="pt-4">{children}</div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

type NewsletterStatus = "idle" | "loading" | "success";

interface NewsletterFormProps {
  email: string;
  status: NewsletterStatus;
  onEmailChange: (email: string) => void;
  onSubmit: (e: FormEvent) => void;
}

/**
 * Presentational only — state lives in Footer so the two responsive copies
 * FooterSection renders (mobile accordion + desktop column) stay in sync
 * instead of each tracking its own independent email/status.
 */
function NewsletterForm({ email, status, onEmailChange, onSubmit }: NewsletterFormProps) {
  const { t } = useTranslation();

  if (status === "success") {
    return (
      <div
        role="status"
        aria-live="polite"
        className="mt-4 flex items-center gap-2 rounded-full bg-white/5 px-4 py-2.5 text-sm text-foam-50 ring-1 ring-white/10"
      >
        <SuccessCheckmark className="h-4 w-4 shrink-0 text-teal-300" />
        {t("footer.newsletterSuccess")}
      </div>
    );
  }

  return (
    <form onSubmit={onSubmit} className="mt-4 flex overflow-hidden rounded-full bg-white/5 ring-1 ring-white/10">
      <input
        type="email"
        required
        aria-label={t("footer.emailAddress")}
        value={email}
        onChange={(e) => onEmailChange(e.target.value)}
        placeholder="you@email.com"
        className="w-full bg-transparent px-4 py-2.5 text-sm text-foam-50 placeholder:text-foam-100/40 focus:outline-none"
      />
      <button
        type="submit"
        disabled={status === "loading"}
        className="whitespace-nowrap bg-teal-400 px-4 text-sm font-semibold text-ocean-950 transition-colors hover:bg-teal-300 disabled:opacity-60"
      >
        {status === "loading" ? t("footer.sending") : t("footer.subscribe")}
      </button>
    </form>
  );
}

export default function Footer() {
  const appSettings = useAppSettings();
  const socialLinks = SOCIAL_ICONS.flatMap(({ Icon, label, key }) => { const href = appSettings?.social[key]; return href ? [{ Icon, label, href }] : []; });
  const { t } = useTranslation();
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState<NewsletterStatus>("idle");
  const { openPreferences } = useCookieConsent();
  const mounted = useRef(true);
  useEffect(() => () => {
    mounted.current = false;
  }, []);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setStatus("loading");
    try {
      await subscribeToNewsletter(email);
      if (mounted.current) setStatus("success");
    } catch {
      if (mounted.current) setStatus("idle");
    }
  };

  return (
    <footer className="bg-ocean-950 pt-16 text-foam-100/80">
      <div className="mx-auto max-w-6xl px-6">
        <div className="grid gap-2 pb-12 md:grid-cols-[1.4fr_1fr_1fr_1.2fr] md:gap-12">
          <div className="pb-4 md:pb-0">
            <div className="flex items-center gap-2 font-display text-lg font-bold text-foam-50">
              <img src="/logo.png" alt="ZenFresh" className="h-10 w-10 object-contain" />
              ZenFresh
            </div>
            <p className="mt-4 max-w-xs text-sm leading-relaxed">{t("footer.tagline")}</p>
            <div className="mt-5 flex gap-3">
              {socialLinks.map(({ Icon, label, href }) => (
                <a
                  key={label}
                  href={href}
                  target="_blank"
                  rel="noreferrer"
                  aria-label={label}
                  className="flex h-9 w-9 items-center justify-center rounded-full bg-white/5 text-foam-100/70 transition-colors hover:bg-teal-400/20 hover:text-teal-300"
                >
                  <Icon className="h-4 w-4" />
                </a>
              ))}
            </div>
          </div>

          <FooterSection title={t("footer.explore")}>
            <ul className="space-y-3 text-sm">
              {EXPLORE.map((item) => (
                <li key={item.to}>
                  <Link to={item.to} className="transition-colors hover:text-teal-300">
                    {t(item.key)}
                  </Link>
                </li>
              ))}
            </ul>
          </FooterSection>

          <FooterSection title={t("footer.company")}>
            <ul className="space-y-3 text-sm">
              {COMPANY.map((item) => (
                <li key={item.to}>
                  <Link to={item.to} className="transition-colors hover:text-teal-300">
                    {t(item.key)}
                  </Link>
                </li>
              ))}
            </ul>
          </FooterSection>

          <FooterSection title={t("footer.stayInTheLoop")}>
            <p className="text-sm">{t("footer.newsletterBody")}</p>
            <NewsletterForm email={email} status={status} onEmailChange={setEmail} onSubmit={handleSubmit} />
          </FooterSection>
        </div>

        <div className="flex flex-col items-center justify-between gap-4 border-t border-white/10 py-6 text-xs text-foam-100/50 sm:flex-row">
          <span>{t("footer.copyright", { year: new Date().getFullYear() })}</span>
          <div className="flex items-center gap-4">
            <button type="button" onClick={openPreferences} className="transition-colors hover:text-teal-300">
              {t("footer.cookiePreferences")}
            </button>
            <span>{t("footer.traceableTagline")}</span>
          </div>
        </div>
      </div>
    </footer>
  );
}
